import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  getAuth,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import Router from 'next/router';
import { EVENT_NAMES, sendEvent } from '../../../utils/analytics';
import { sendEmailVerification } from '../../../utils/api';
import { getSignUpWithEmailLink } from '../../../utils/api/authentication';
import firebaseApp from '../../../utils/firebase';
import { captureException } from '../../../utils/tracking/sentry/sentry';
import { getEmail } from './loginSelectors';
import { LoginModuleState, Step } from './loginSlice';

export const logInWithPassword = createAsyncThunk<void, { email: string; password: string }>(
  'login/logInWithPassword',
  async ({ email, password }) => {
    try {
      const auth = await getAuth(firebaseApp);
      await signInWithEmailAndPassword(auth, email, password);
      // authenticate action is listening to firebase user changes
      // no need to handle anything here
      // it is loaded by default in the login page
    } catch (error) {
      sendEvent({
        name: EVENT_NAMES.loginLoginFailed,
        properties: {
          method: 'password',
          error,
        },
      });
      throw error;
    }
  },
);

export const createSignInWithEmailLink = createAsyncThunk<void, { email: string }>(
  'login/createSignInWithEmailLink',
  async ({ email }) => {
    try {
      await getSignUpWithEmailLink({ email });
      localStorage.setItem('@Auth:storedEmail', email);
    } catch (error) {
      sendEvent({
        name: EVENT_NAMES.loginLoginFailed,
        properties: {
          method: 'email-link',
          error,
        },
      });
      throw error;
    }
  },
);

export const checkSignUpEmail = createAsyncThunk<
  {
    step?: Step;
    providers: string[];
    error?: { code: 'AlreadyHasPassword' | 'AlreadyHasOtherProviders' };
  },
  { email: string }
>('login/checkSignUpEmail', async ({ email }) => {
  try {
    const auth = await getAuth(firebaseApp);
    const providers = await fetchSignInMethodsForEmail(auth, email);
    if (providers.includes('password')) {
      return {
        error: {
          code: 'AlreadyHasPassword',
        },
        providers,
      };
    }
    if (providers.length) {
      return {
        error: {
          code: 'AlreadyHasOtherProviders',
        },
        providers,
      };
    }
    return {
      providers,
      step: 'create-password',
    };
  } catch (error) {
    sendEvent({
      name: EVENT_NAMES.checkSignUpEmailFailed,
      properties: {
        error,
      },
    });
    captureException(error);
    throw error;
  }
});

export const createPassword = createAsyncThunk<
  void,
  { password: string },
  { state: LoginModuleState }
>('login/createPassword', async ({ password }, { getState }) => {
  try {
    const email = getEmail(await getState());
    if (!email) {
      throw new Error('no email');
    }
    const auth = await getAuth(firebaseApp);
    const credentials = await createUserWithEmailAndPassword(auth, email, password);

    const query: typeof Router.query = { ...Router.query, email };
    delete query.step;
    await Router.replace({
      pathname: Router.pathname,
      query,
    });

    const firebaseToken = await credentials.user?.getIdToken(true);
    await sendEmailVerification(firebaseToken, window.location.href);
  } catch (error) {
    sendEvent({
      name: EVENT_NAMES.createPasswordFailed,
      properties: {
        error,
      },
    });
    throw error;
  }
});
